<template>
  <SharedLink
    :link="link"
  >
    <div
      class="card-content"
      :style="backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : ''"
      :title="switchableMode?.imageTitle ?? ''"
      :data-ab-id="dataAbId"
    >
      <div class="card-content__container">
        <span>{{ headline }}</span>
        <h2
          v-if="title"
          class="flex items-end gap-1"
        >
          {{ title }}
          <KikLegalInformationDrawer
            v-if="enableLegalDisclaimer && titleOrSubtitle === 'title'"
            :legal-text="legalText"
            :title="legalTitle"
          />
        </h2>
        <p class="flex items-end gap-1">
          {{ subHeadline }}
          <KikLegalInformationDrawer
            v-if="enableLegalDisclaimer && titleOrSubtitle === 'subtitle'"
            :legal-text="legalText"
            :title="legalTitle"
          />
        </p>
        <div class="card-content__buttons-container">
          <SharedLink
            v-for="(ctaLink, i) in ctaLinks"
            :key="i"
            :link="ctaLink.link"
            class="rounded-md border px-4 py-2 mobile:!text-base"
            :style="{ color: `${ctaLink.titleColor}`, borderColor: `${ctaLink.borderColor}`, backgroundColor: `${ctaLink.buttonColor}` }"
          >
            {{ ctaLink.title }}
          </SharedLink>
        </div>
      </div>
    </div>
  </SharedLink>
</template>

<script lang="ts" setup>
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

interface Props {
  title?: string;
  titleColor?: string;
  headline?: string;
  headlineColor?: string;
  subHeadline?: string;
  subHeadlineColor?: string;
  switchableMode?: any;
  ctas?: any[];
  intLinkParam?: string;
  imageLoadType?: HTMLImageElement['loading'];
  dataAbId?: string;
  legalText?: string;
  legalTitle?: string;
  enableLegalDisclaimer?: boolean;
  titleOrSubtitle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  titleColor: '',
  headline: '',
  headlineColor: '',
  subHeadline: '',
  subHeadlineColor: '',
  switchableMode: () => ({}),
  ctas: () => ([]),
  intLinkParam: '',
  imageLoadType: 'lazy',
  dataAbId: '',
  legalText: '',
  legalTitle: '',
  enableLegalDisclaimer: false,
  titleOrSubtitle: '',
});

const { $domain } = useNuxtApp();

const {
  switchableMode, ctas, imageLoadType,
} = toRefs(props);

const backgroundColor = computed(() => (
  switchableMode.value.field === 'backgroundColor' ? `#${(16777216 + switchableMode.value.backgroundColor)?.toString(16)?.padStart(6, '0')}` : null
));

const backgroundImage = computed(() => (
  switchableMode.value.field === 'image' ? magnoliaGetters.getDefaultSrcAsset(switchableMode.value.image, $domain.cmsAssetDomainPath) : ''
));

const link = computed(() => (switchableMode.value?.field === 'image' && switchableMode.value?.imageLink?.length ? magnoliaGetters.getLinkAndName(switchableMode.value.imageLink?.[0], $domain.cmsAssetDomainPath, props.intLinkParam) : {}));

const ctaLinks = computed(() => (
  ctas.value?.map(cta => ({
    link: magnoliaGetters.getLinkAndName(cta, $domain.cmsAssetDomainPath, props.intLinkParam),
  }))
));

if (switchableMode.value.field === 'image' && imageLoadType.value === 'eager') {
  useHead({
    link: [
      {
        rel: 'preload',
        as: 'image',
        href: backgroundImage.value ?? '',
      },
    ],
  });
}
</script>

<style lang="scss" scoped>
.card-content {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: var(--spacer-base) 0;
  padding: 50px 110px;
  background-size: cover;
  background-position: 50%;
  background-color: v-bind(backgroundColor);

  @include only-mobile {
    padding: 20px;
  }

  span {
    color: v-bind(headlineColor);
    font-size: var(--font-size--sm);
    text-transform: uppercase;
  }

  h2 {
    color: v-bind(titleColor);
    font-size: 30px;
    font-weight: 700;
  }

  p {
    color: v-bind(subHeadlineColor);
    font-size: var(--font-size--base);
    text-transform: uppercase;
    margin: 10px 0 0;
  }

  &__container {
    display: flex;
    padding: 30px;
    flex-direction: column;
    align-items: center;
    position: relative;
    background: #ffffff;
    width: 100%;

    @include only-mobile {
      text-align: center;
      padding: 20px;
    }
  }

  &__buttons-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
    width: 100%;
    margin-top: 16px;
  }
}
</style>
